import { FormProvider, Input, Stack, useForm } from '@qred/components-library';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import { FormErrorBanner } from '~/components/shared/FormErrorBanner/FormErrorBanner';
import { TurnoverSelect } from '~/components/shared/TurnoverSelect/TurnoverSelect';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { ICompanySearchResult } from '~/interfaces/shared';
import { RootState } from '~/store';
import { updateForm } from '~/store/slices/cardApplication.slice';
import {
  CompanySelectorForm,
  companySelectorSchema,
} from './CompanySelector.schema';
import { CompanySearch } from './components/CompanySearch';

export const CompanySelector = () => {
  const {
    organizationNumber,
    companyName,
    companyType,
    turnover,
  } = useSelector((state: RootState) => state.cardApplication.form);
  const validationContext = useContext(ValidationContext);
  const [
    companyDetails,
    setCompanyDetails,
  ] = useState<ICompanySearchResult | null>(
    organizationNumber && companyName
      ? {
          companyName,
          companyNumber: organizationNumber,
          companyType,
        }
      : null
  );

  const dispatch = useAppDispatch();

  const formMethods = useForm<CompanySelectorForm>({
    schema: companySelectorSchema,
    defaultValues: {
      selectedCompany: organizationNumber,
      turnover: turnover ?? '',
    },
    mode: 'onTouched',
  });

  const { formState, getValues } = formMethods;

  useEffect(() => {
    if (!formState.isValid) {
      validationContext.manuallyInvalidateForm(true);
    }

    return () => {
      validationContext.manuallyInvalidateForm(false);
    };
  }, [formState.isValid]);

  useEffect(() => {
    if (!formState.isValidating && formState.isValid) {
      dispatch(
        updateForm({
          organizationNumber: getValues('selectedCompany'),
          companyName: companyDetails?.companyName || '',
          companyType: companyDetails?.companyType || '',
          turnover: getValues('turnover'),
        })
      );
    }
  }, [formState.isValid, formState.isValidating, dispatch]);

  return (
    <Stack spacing={'xxl'}>
      <FormProvider {...formMethods}>
        <Input.Group orientation="vertical">
          <CompanySearch
            defaultCompanyDetails={companyDetails}
            setCompanyDetails={setCompanyDetails}
          />
          <TurnoverSelect />
        </Input.Group>
      </FormProvider>
      <FormErrorBanner errors={formState.errors} />
    </Stack>
  );
};
